import { AddCircle } from "src/resources/icons";
import {
  Flex,
  SimpleGrid,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { MemberDetails, ProfileDto } from "src/types/Profile";
import { PROFILE_HEADINGS_ENG } from "src/common/constants/englishLabels";
import React, { useEffect, useState } from "react";

import { PROFILE_HEADINGS_HIN } from "src/common/constants/hindiLabels";
import RightAlignedModal from "./rightAlignedModal";
import FamilyMemberCard from "./FamilyMemberCard";

interface FamilyProps {
  isOpenedFromAdmin: boolean;
  userData: ProfileDto | null;
  isHindiLabels?: boolean; // optional prop for language toggle
}

export const Family: React.FC<FamilyProps> = ({ userData, isHindiLabels = false, isOpenedFromAdmin }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [edit, setEdit] = useState(false);
  const [localUser, setLocalUser] = useState<ProfileDto | null>(userData);
  const [selectedMember, setSelectedMember] = useState<MemberDetails | null>(null);

  useEffect(() => {
    setLocalUser(userData || null);
  }, [userData]);

  const family = localUser?.familyDetails;
  const personal = localUser?.personalDetails;
  const members: Array<MemberDetails | any> = (localUser as any)?.members || [];

  const hasFather = !!(personal?.fatherName || family?.fatherName);
  const hasMother = !!(personal?.motherName || family?.motherName);

  return (
    <Flex direction="column" justifyContent="flex-end" mt="2rem" mr="-1rem">
      {/* Add Family Member Button */}
      <Flex width="100%" justifyContent="flex-end">
        {!isOpenedFromAdmin &&  (<Flex
          margin={2}
          onClick={() => {
            setEdit(false);
            onOpen();
          }}
          borderRadius="5px"
          background="rgba(108, 117, 125, 0.10)"
          gap="8px"
          padding="14px"
          _hover={{ cursor: "pointer" }}
        >
          <AddCircle />
          <Text>
            {isHindiLabels
              ? PROFILE_HEADINGS_HIN.ADD_FAMILY_MEMBER
              : PROFILE_HEADINGS_ENG.ADD_FAMILY_MEMBER}
          </Text>
        </Flex>)}
      </Flex>

      {/* Family Members Display */}
      {(hasFather || hasMother || members.length > 0) ? (
        <SimpleGrid columns={{ sm: 1, md: 2, lg: 3 }} spacing="1rem">
          {/* Father */}
          {hasFather && (
            <FamilyMemberCard
              name={personal?.fatherName || family?.fatherName || "—"}
              relation="Father"
              isOpenedFromAdmin={isOpenedFromAdmin}
              onEdit={() => {
                setEdit(true);
                setSelectedMember({
                  memberName: personal?.fatherName || family?.fatherName || "",
                  relation: "Father",
                  email: "",
                  mobileNo: "",
                } as any);
                onOpen();
              }}
            />
          )}

          {/* Mother */}
          {hasMother && (
            <FamilyMemberCard
              name={personal?.motherName || family?.motherName || "—"}
              relation="Mother"
              isOpenedFromAdmin={isOpenedFromAdmin}
              onEdit={() => {
                setEdit(true);
                setSelectedMember({
                  memberName: personal?.motherName || family?.motherName || "",
                  relation: "Mother",
                  email: "",
                  mobileNo: "",
                } as any);
                onOpen();
              }}
            />
          )}

          {/* Other Members from backend */}
          {members.map((m, idx) => (
            <FamilyMemberCard
              key={`${(m as any).id || (m as any).memberName || idx}`}
              name={(m as any).memberName || "—"}
              relation={(m as any).relation || "—"}
              email={(m as any).email || undefined}
              isOpenedFromAdmin={isOpenedFromAdmin}
              onEdit={() => {
                setEdit(true);
                setSelectedMember({
                  id: (m as any).id,
                  memberName: (m as any).memberName || "",
                  relation: (m as any).relation || "",
                  email: (m as any).email || "",
                  mobileNo: (m as any).mobileNo || "",
                } as any);
                onOpen();
              }}
            />
          ))}
        </SimpleGrid>
      ) : (
        <Text color="gray.500">No family details found.</Text>
      )}

      {/* Right-Aligned Modal */}
      <RightAlignedModal isOpen={isOpen} onClose={onClose} view={edit ? "EDIT_MEMBER" : "FAMILY"} userData={localUser} selectedMember={selectedMember as any} onSave={(updated) => { setLocalUser(updated); setEdit(false); setSelectedMember(null); }} />
    </Flex>
  );
};

export default Family;
